import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Grid,
  Button,
  Divider,
  Chip,
  TextField,
  Stack,
  Link as MuiLink,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PhoneIcon from "@mui/icons-material/Phone";
import dayjs from "dayjs";
import {
  getBookingDetails,
  markNoShow,
  updateBookingComment,
  updateBookingFundsHolder,
  manualCancelBookingById,
  refundBooking,
  confirmRefundBooking,
} from "../../api/bookings";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { getCityDisplay } from "../../utils/city";

const STATUS_UK_TO_KEY = {
  Відбулася: "completed",
  Оплачено: "paid",
  Відбудеться: "scheduled",
  "Очікує оплату": "pending",
  "Обробка платежу": "processing",
  "Повернення коштів в процесі": "refunding",
  Повернено: "refunded",
  "Не оплачено": "not_paid",
  "Помилка оплати": "failed",
  Скасовано: "cancelled",
  "Час на оплату вийшов": "expired",
  "Не прибув на рейс": "no_show",
};

const normalizeStatusKey = (status) =>
  STATUS_UK_TO_KEY[status] || String(status || "").toLowerCase();

// no_show — явка, не результат оплати. Картка вже списана; на місці грошей не було.
const moneyStatusKey = (booking) => {
  const key = normalizeStatusKey(booking?.payment_status);
  if (key !== "no_show") return null;
  return booking?.payment_method === "on_site" ? "not_paid" : "paid";
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const parsed = dayjs(value, "YYYY-MM-DD HH:mm");
  if (parsed.isValid()) return parsed.format("DD.MM.YYYY HH:mm");
  const fallback = dayjs(value);
  return fallback.isValid() ? fallback.format("DD.MM.YYYY HH:mm") : String(value);
};

export default function AdminBookingDetails() {
  const { t } = useTranslation("common");
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelLegIndex, setCancelLegIndex] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const [refundLeg, setRefundLeg] = useState(null);
  const [refunding, setRefunding] = useState(false);
  const [confirmLeg, setConfirmLeg] = useState(null);
  const [confirmingRefund, setConfirmingRefund] = useState(false);
  const [noShowDialogOpen, setNoShowDialogOpen] = useState(false);
  const [markingNoShow, setMarkingNoShow] = useState(false);
  const [noShowComment, setNoShowComment] = useState("");
  const [commentEditMode, setCommentEditMode] = useState(false);
  const [comment, setComment] = useState("");
  const [updatingComment, setUpdatingComment] = useState(false);
  const [savingFundsHolder, setSavingFundsHolder] = useState(false);

  useEffect(() => {
    async function fetchBooking() {
      try {
        const data = await getBookingDetails(id);
        setBooking(data);
        setComment(data.comment || "");
      } catch (error) {
        navigate("/administrator/bookings");
      } finally {
        setLoading(false);
      }
    }
    fetchBooking();
  }, [id, navigate]);

  const renderStatusChip = (status) => {
    const key = normalizeStatusKey(status);
    const colorMap = {
      completed: "success",
      paid: "success",
      scheduled: "info",
      pending: "info",
      processing: "info",
      refunding: "warning",
      refunded: "warning",
      not_paid: "warning",
      failed: "error",
      cancelled: "error",
      expired: "warning",
      no_show: "warning",
    };
    const variantMap = {
      paid: "filled",
      refunding: "filled",
      failed: "filled",
      cancelled: "filled",
      no_show: "filled",
    };
    const knownKeys = [
      "paid",
      "pending",
      "processing",
      "not_paid",
      "cancelled",
      "expired",
      "no_show",
      "refunded",
      "refunding",
      "failed",
      "completed",
      "scheduled",
    ];
    const label = knownKeys.includes(key) ? t(`status.${key}`) : status;
    return (
      <Chip
        label={label}
        color={colorMap[key] || "default"}
        variant={variantMap[key] || "outlined"}
        size="small"
      />
    );
  };

  const paymentMethodLabel = (method) => {
    if (method === "card") return t("admin.paymentCard");
    if (method === "on_site") return t("admin.paymentOnSite");
    return method || "—";
  };

  const paymentKey =
    normalizeStatusKey(booking?.payment_status_display) || booking?.payment_status;
  const tripKey =
    normalizeStatusKey(booking?.trip_status_display) ||
    normalizeStatusKey(booking?.trip_status);

  const canCancel =
    tripKey === "scheduled" &&
    paymentKey !== "cancelled" &&
    paymentKey !== "expired" &&
    paymentKey !== "no_show";
  const legPayKey = (leg) =>
    leg?.payment_status || normalizeStatusKey(leg?.payment_status_display);
  const legTripKey = (leg) => normalizeStatusKey(leg?.trip_status);
  const closedLeg = ["cancelled", "expired", "refunded", "refunding", "failed", "no_show"];
  const legCanCancel = (leg) =>
    legTripKey(leg) === "scheduled" && !closedLeg.includes(legPayKey(leg));
  const legCanRefund = (leg) =>
    booking?.payment_method === "card" &&
    legPayKey(leg) === "paid" &&
    legTripKey(leg) === "scheduled";
  const legCanConfirmRefund = (leg) =>
    booking?.payment_method === "card" &&
    legPayKey(leg) === "refunding" &&
    leg?.refund_amount != null &&
    Number(leg.refund_amount) > 0;
  const canConfirmBookingRefund =
    !booking?.is_round_trip &&
    !booking?.is_transfer &&
    booking?.payment_method === "card" &&
    paymentKey === "refunding" &&
    booking?.refund_amount != null &&
    Number(booking.refund_amount) > 0;
  const legAwaitingTransferRefund = (leg) =>
    booking?.is_transfer &&
    booking?.payment_method === "card" &&
    legPayKey(leg) === "refunding" &&
    leg?.refund_amount != null &&
    Number(leg.refund_amount) > 0 &&
    !(leg.passengers || []).some(
      (p) => p.refund_status === "sent" || p.refund_status === "confirmed"
    );
  const transferRefundCents = (booking?.legs || []).reduce(
    (sum, leg) =>
      legAwaitingTransferRefund(leg)
        ? sum + Math.round(Number(leg.refund_amount) * 100)
        : sum,
    0
  );
  const canConfirmTransferRefund = transferRefundCents > 0;
  const transferRefundAmount = transferRefundCents / 100;
  const moneyLegs = booking?.legs?.length ? booking.legs : booking ? [booking] : [];
  const moneyCents = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
  };
  const legRefundCents = (leg) => {
    const people = leg?.passengers || [];
    const rows = people.filter(
      (p) => p.refund_amount != null && Number(p.refund_amount) > 0
    );
    let refunded = 0;
    let awaiting = 0;
    if (rows.length) {
      rows.forEach((p) => {
        const part = moneyCents(p.refund_amount);
        if (p.refund_status === "confirmed" || p.status === "refunded") refunded += part;
        else awaiting += part;
      });
      return { refunded, awaiting };
    }
    const legStatus = legPayKey(leg);
    if (
      (legStatus === "refunding" || legStatus === "refunded") &&
      leg?.refund_amount != null
    ) {
      const part = moneyCents(leg.refund_amount);
      if (legStatus === "refunded") refunded += part;
      else awaiting += part;
    }
    return { refunded, awaiting };
  };
  const refundedCents = moneyLegs.reduce(
    (sum, leg) => sum + legRefundCents(leg).refunded,
    0
  );
  const awaitingCents = moneyLegs.reduce(
    (sum, leg) => sum + legRefundCents(leg).awaiting,
    0
  );
  const hasRefundMovement = refundedCents > 0 || awaitingCents > 0;
  const fromCents = (cents) => cents / 100;
  const reloadBooking = async () => {
    const data = await getBookingDetails(booking.id);
    setBooking(data);
    setComment(data.comment || "");
  };
  const passengers = booking?.passengers || [];
  const passengerRows =
    booking?.is_round_trip || booking?.is_transfer
      ? (booking.legs || []).flatMap((leg) =>
          (leg.passengers || []).map((p) => ({
            ...p,
            leg_index: leg.leg_index,
            direction: booking.is_round_trip
              ? leg.leg_index === 1
                ? t("roundTrip.outbound")
                : t("roundTrip.return")
              : t("adminDetails.legN", { n: leg.leg_index }),
          }))
        )
      : passengers;
  const moneyLabel = (amount) => t("units.uahAmount", { amount });
  const campaignLabel = (booking?.campaign_label || booking?.campaign_name || "").trim();
  const promoCode = (booking?.promo_code || "").trim();
  const offerDiscount = Number(booking?.discount_uah) || 0;
  const appliedOffer =
    offerDiscount > 0 && (campaignLabel || promoCode)
      ? {
          label: campaignLabel,
          promoCode,
          promoOnly: !campaignLabel && Boolean(promoCode),
          discount: offerDiscount,
          subtotal:
            booking?.subtotal_uah != null && booking.subtotal_uah !== ""
              ? Number(booking.subtotal_uah)
              : null,
        }
      : null;
  const refundMark = (p) => {
    const amount =
      p.refund_amount != null && Number(p.refund_amount) > 0
        ? moneyLabel(p.refund_amount)
        : "";
    const withAmount = (label) => (amount ? `${label} · ${amount}` : label);
    if (p.status === "refunded" || p.refund_status === "confirmed") {
      return withAmount(t("adminDetails.passengerRefunded"));
    }
    if (p.status === "refunding" && p.refund_status === "sent") {
      return withAmount(t("adminDetails.refundAtLiqpay"));
    }
    if (p.status === "refunding" && p.refund_status !== "pending") {
      return withAmount(t("adminDetails.awaitingRefundSum"));
    }
    return "";
  };
  const boardingLabel = (p) =>
    p.boarding_status === "boarded"
      ? t("admin.boardingBoarded")
      : p.boarding_status === "no_show"
        ? t("admin.boardingNoShow")
        : t("admin.boardingPending");
  const confirmPassengerRefund = async (p) => {
    try {
      await confirmRefundBooking(booking.id, p.leg_index, p.id);
      enqueueSnackbar(t("adminDetails.liqpayRefundSent"), { variant: "success" });
      await reloadBooking();
    } catch (err) {
      enqueueSnackbar(err?.message || t("adminDetails.refundError"), { variant: "error" });
    }
  };
  const allPassengersNoShow =
    passengers.length > 0 &&
    passengers.every((p) => p.boarding_status === "no_show");
  const canMarkNoShow =
    tripKey === "completed" && paymentKey !== "cancelled" && !allPassengersNoShow;

  const copyBookingLink = () => {
    const link = `${window.location.origin}/booking/${booking.id}`;
    navigator.clipboard
      .writeText(link)
      .then(() => enqueueSnackbar(t("admin.linkCopied"), { variant: "success" }))
      .catch(() => enqueueSnackbar(t("admin.copyLinkError"), { variant: "error" }));
  };

  const saveComment = async () => {
    try {
      setUpdatingComment(true);
      await updateBookingComment(booking.id, comment);
      setBooking({ ...booking, comment });
      setCommentEditMode(false);
      enqueueSnackbar(
        booking.comment ? t("adminDetails.commentUpdated") : t("adminDetails.commentAdded"),
        { variant: "success" }
      );
    } catch (err) {
      enqueueSnackbar(t("adminDetails.commentAddError"), { variant: "error" });
    } finally {
      setUpdatingComment(false);
    }
  };

  const renderStopBlock = (label, stop) => (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={600}>
        {getCityDisplay(stop?.city) || "—"}
      </Typography>
      {stop?.address ? (
        <Typography variant="body2" color="text.secondary">
          {stop.address}
        </Typography>
      ) : null}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {formatDateTime(stop?.departure_datetime || stop?.arrival_datetime)}
      </Typography>
    </Box>
  );

  const renderLegCard = (leg, index) => {
    const legTripKey = normalizeStatusKey(leg?.trip_status);
    return (
      <Paper
        key={`${leg.id}-${leg.leg_index || index}`}
        variant="outlined"
        sx={{ p: 2, borderRadius: 2, mb: 1.5 }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          gap={1}
          mb={1.5}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            {leg.title || t("adminDetails.legN", { n: leg.leg_index || index + 1 })}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {leg.payment_status_display || leg.payment_status
              ? renderStatusChip(leg.payment_status_display || leg.payment_status)
              : null}
            {legTripKey ? renderStatusChip(leg.trip_status) : null}
            {leg.price != null ? (
              <Box textAlign={{ xs: "left", sm: "right" }}>
                <Typography variant="body2" fontWeight={600}>
                  {t("units.uahAmount", { amount: leg.price })}
                </Typography>
                {legRefundCents(leg).refunded > 0 ? (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t("adminDetails.refundedOf", {
                      amount: t("units.uahAmount", {
                        amount: fromCents(legRefundCents(leg).refunded),
                      }),
                    })}
                  </Typography>
                ) : null}
                {legRefundCents(leg).awaiting > 0 ? (
                  <Typography variant="caption" color="warning.main" display="block">
                    {t("adminDetails.awaitingOf", {
                      amount: t("units.uahAmount", {
                        amount: fromCents(legRefundCents(leg).awaiting),
                      }),
                    })}
                  </Typography>
                ) : null}
                {leg.partner_price != null &&
                leg.partner_currency &&
                String(leg.partner_currency).toUpperCase() !== "UAH" ? (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t("units.agentPrice", {
                      amount: leg.partner_price,
                      currency: leg.partner_currency,
                    })}
                  </Typography>
                ) : null}
              </Box>
            ) : null}
            {leg.trip_id ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate(`/administrator/trips/${leg.trip_id}/passengers`)}
              >
                {t("adminDetails.viewTripPassengers")}
              </Button>
            ) : null}
          </Stack>
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            {renderStopBlock(t("adminDetails.from"), leg.from)}
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderStopBlock(t("adminDetails.to"), {
              ...leg.to,
              departure_datetime: undefined,
            })}
          </Grid>
        </Grid>
        {booking?.is_round_trip ||
        (legCanConfirmRefund(leg) && !booking?.is_transfer) ? (
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
            {legCanCancel(leg) ? (
              <Button
                size="small"
                color="error"
                variant="outlined"
                onClick={() => {
                  setCancelLegIndex(leg.leg_index);
                  setCancelDialogOpen(true);
                }}
              >
                {t("adminDetails.cancelDirection")}
              </Button>
            ) : null}
            {legCanRefund(leg) ? (
              <Button
                size="small"
                color="warning"
                variant="outlined"
                onClick={() => setRefundLeg(leg)}
              >
                {t("adminDetails.refundDirection")}
              </Button>
            ) : null}
            {legCanConfirmRefund(leg) ? (
              <Button
                size="small"
                color="warning"
                variant="contained"
                onClick={() => setConfirmLeg(leg)}
              >
                {t("adminDetails.confirmLiqpay")}
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Paper>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!booking) {
    return <Typography align="center">{t("booking.notFound")}</Typography>;
  }

  const routeLabel = `${getCityDisplay(booking.from?.city) || "—"} — ${
    getCityDisplay(booking.to?.city) || "—"
  }`;

  return (
    <Container maxWidth="md" sx={{ mt: 2, mb: 6 }}>
      <MuiLink
        component={RouterLink}
        to="/administrator/bookings"
        underline="hover"
        variant="body2"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 1 }}
      >
        <ArrowBackIcon fontSize="inherit" />
        {t("adminDetails.backToList")}
      </MuiLink>

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "flex-start" }}
          gap={2}
          mb={2}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              #{booking.id}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {routeLabel}
              {booking.is_round_trip ? ` · ${t("search.roundTrip")}` : ""}
              {booking.is_transfer ? ` · ${t("admin.withTransfer")}` : ""}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
              {booking.leg_statuses?.length > 1
                ? booking.leg_statuses.map((leg) => (
                    <Stack
                      key={leg.leg_index}
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                    >
                      <Typography variant="caption" color="text.secondary">
                        {leg.leg_index === 2
                          ? t("roundTrip.return")
                          : t("roundTrip.outbound")}
                      </Typography>
                      {renderStatusChip(
                        leg.payment_status_display || leg.payment_status
                      )}
                    </Stack>
                  ))
                : renderStatusChip(
                    booking.payment_status_display || booking.payment_status
                  )}
              {booking.is_transfer ? (
                <Chip label={t("admin.withTransfer")} size="small" variant="outlined" />
              ) : (
                renderStatusChip(booking.trip_status)
              )}
            </Stack>
          </Box>
          <Stack direction="row" spacing={1}>
            {booking.buyer_phone ? (
              <Button
                variant="outlined"
                size="small"
                startIcon={<PhoneIcon />}
                href={`tel:${booking.buyer_phone}`}
                component="a"
              >
                {t("admin.call")}
              </Button>
            ) : null}
            <IconButton onClick={copyBookingLink} aria-label={t("admin.copyLink")}>
              <ContentCopyIcon />
            </IconButton>
          </Stack>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {t("adminDetails.contactsSection")}
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              {t("adminDetails.buyerPhone")}
            </Typography>
            <Typography>
              {booking.buyer_phone ? (
                <MuiLink href={`tel:${booking.buyer_phone}`} underline="hover">
                  {booking.buyer_phone}
                </MuiLink>
              ) : (
                "—"
              )}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              {t("adminDetails.buyerEmail")}
            </Typography>
            <Typography>{booking.buyer_email || "—"}</Typography>
          </Grid>
          {booking.company ? (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                {t("admin.company")}
              </Typography>
              <Typography>{booking.company}</Typography>
            </Grid>
          ) : null}
          {booking.created_at ? (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                {t("admin.created")}
              </Typography>
              <Typography>{booking.created_at}</Typography>
            </Grid>
          ) : null}
        </Grid>

        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {booking.is_round_trip
            ? t("search.roundTrip")
            : booking.is_transfer
              ? t("adminDetails.itinerary")
              : t("adminDetails.tripSection")}
        </Typography>
        {(booking.is_transfer || booking.is_round_trip) && booking.legs?.length ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {booking.is_round_trip
                ? t("adminDetails.roundTripSummary")
                : t("adminDetails.transferSummary", {
                    from: getCityDisplay(booking.from?.city),
                    to: getCityDisplay(booking.to?.city),
                    count: booking.legs.length,
                  })}
            </Typography>
            {booking.legs.map((leg, idx) =>
              renderLegCard(
                {
                  ...leg,
                  title: booking.is_round_trip
                    ? leg.leg_index === 2
                      ? t("roundTrip.return")
                      : t("roundTrip.outbound")
                    : null,
                },
                idx
              )
            )}
          </>
        ) : (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                {renderStopBlock(t("adminDetails.from"), booking.from)}
              </Grid>
              <Grid item xs={12} sm={6}>
                {renderStopBlock(t("adminDetails.to"), {
                  ...booking.to,
                  departure_datetime: undefined,
                })}
              </Grid>
            </Grid>
            {booking.trip_id ? (
              <Button
                size="small"
                variant="outlined"
                sx={{ mt: 2 }}
                onClick={() => navigate(`/administrator/trips/${booking.trip_id}/passengers`)}
              >
                {t("adminDetails.viewTripPassengers")}
              </Button>
            ) : null}
          </Paper>
        )}

        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
          {t("adminDetails.passengers")}
        </Typography>
        <Stack spacing={1.5} sx={{ display: { xs: "flex", sm: "none" }, mb: 3 }}>
          {passengerRows.map((p) => (
            <Box
              key={`m-${p.leg_index || 0}-${p.id}`}
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                p: 1.5,
              }}
            >
              <Typography fontWeight={600}>
                {p.direction ? `${p.direction}: ` : ""}
                {p.name} {p.surname}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {p.type === "child" ? t("adminDetails.child") : t("adminDetails.adult")}
                {refundMark(p)
                  ? ` · ${refundMark(p)}`
                  : p.status === "refunding"
                    ? ""
                    : ` · ${boardingLabel(p)}`}
              </Typography>
              {p.status === "refunding" &&
              p.refund_status === "pending" &&
              !canConfirmTransferRefund ? (
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  {p.refund_amount != null && Number(p.refund_amount) > 0 ? (
                    <Typography variant="caption" color="warning.main">
                      {t("adminDetails.awaitingOf", {
                        amount: moneyLabel(p.refund_amount),
                      })}
                    </Typography>
                  ) : null}
                  <Button
                    size="small"
                    color="warning"
                    variant="outlined"
                    fullWidth
                    onClick={() => confirmPassengerRefund(p)}
                  >
                    {t("adminDetails.confirmLiqpay")}
                  </Button>
                </Stack>
              ) : null}
            </Box>
          ))}
        </Stack>
        <TableContainer sx={{ display: { xs: "none", sm: "block" }, mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("form.name")}</TableCell>
                <TableCell>{t("form.surname")}</TableCell>
                <TableCell>{t("adminDetails.passengerType")}</TableCell>
                <TableCell>{t("admin.boarding")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {passengerRows.map((p) => (
                <TableRow key={`${p.leg_index || 0}-${p.id}`}>
                  <TableCell>
                    {p.direction ? `${p.direction}: ` : ""}
                    {p.name}
                  </TableCell>
                  <TableCell>{p.surname}</TableCell>
                  <TableCell>
                    {p.type === "child" ? t("adminDetails.child") : t("adminDetails.adult")}
                  </TableCell>
                  <TableCell>
                    {refundMark(p) ? (
                      <Typography
                        component="span"
                        variant="body2"
                        color={p.status === "refunded" ? "text.secondary" : "warning.main"}
                      >
                        {refundMark(p)}
                      </Typography>
                    ) : p.status === "refunding" &&
                      p.refund_status === "pending" &&
                      !canConfirmTransferRefund ? (
                      <Stack spacing={0.5} alignItems="flex-start">
                        {p.refund_amount != null && Number(p.refund_amount) > 0 ? (
                          <Typography variant="caption" color="warning.main">
                            {t("adminDetails.awaitingOf", {
                              amount: moneyLabel(p.refund_amount),
                            })}
                          </Typography>
                        ) : null}
                        <Button
                          size="small"
                          color="warning"
                          sx={{ whiteSpace: "nowrap" }}
                          onClick={() => confirmPassengerRefund(p)}
                        >
                          {t("adminDetails.confirmLiqpay")}
                        </Button>
                      </Stack>
                    ) : (
                      boardingLabel(p)
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {t("adminDetails.paymentSection")}
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={3}
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {t("adminDetails.paidSum")}
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {booking.price != null ? moneyLabel(booking.price) : "—"}
            </Typography>
            {appliedOffer ? (
              <Typography variant="caption" color="text.secondary" display="block">
                {appliedOffer.promoOnly
                  ? `${t("checkout.promoDiscount")} (${appliedOffer.promoCode})`
                  : t("checkout.campaignDiscount", { name: appliedOffer.label })}
                {appliedOffer.subtotal != null
                  ? ` · ${t("checkout.fullPrice")} ${moneyLabel(appliedOffer.subtotal)}`
                  : ""}
                {` · −${moneyLabel(appliedOffer.discount)}`}
              </Typography>
            ) : null}
            {booking.partner_price != null &&
            booking.partner_currency &&
            String(booking.partner_currency).toUpperCase() !== "UAH" ? (
              <Typography variant="caption" color="text.secondary" display="block">
                {t("units.agentPrice", {
                  amount: booking.partner_price,
                  currency: booking.partner_currency,
                })}
              </Typography>
            ) : null}
          </Box>
          {hasRefundMovement ? (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {t("adminDetails.refundedSum")}
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {moneyLabel(fromCents(refundedCents))}
              </Typography>
            </Box>
          ) : null}
          {awaitingCents > 0 ? (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {t("adminDetails.awaitingRefundSum")}
              </Typography>
              <Typography variant="h5" fontWeight={700} color="warning.main">
                {moneyLabel(fromCents(awaitingCents))}
              </Typography>
            </Box>
          ) : null}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {t("admin.paymentStatus")}
            </Typography>
            {renderStatusChip(
              moneyStatusKey(booking) ||
                booking.payment_status_display ||
                booking.payment_status
            )}
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {t("adminDetails.paymentMethod")}
            </Typography>
            <Typography>{paymentMethodLabel(booking.payment_method)}</Typography>
          </Box>
          {canConfirmBookingRefund || canConfirmTransferRefund ? (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {t("adminDetails.refundAmount")}
              </Typography>
              <Typography fontWeight={700} sx={{ mb: 1 }}>
                {t("units.uahAmount", {
                  amount: canConfirmTransferRefund
                    ? transferRefundAmount
                    : booking.refund_amount,
                })}
              </Typography>
              <Button
                color="warning"
                variant="contained"
                onClick={() =>
                  setConfirmLeg(
                    canConfirmTransferRefund
                      ? {
                          refund_amount: transferRefundAmount,
                          whole: true,
                          transfer: true,
                        }
                      : {
                          refund_amount: booking.refund_amount,
                          whole: true,
                        }
                  )
                }
              >
                {t("adminDetails.confirmLiqpay")}
              </Button>
            </Box>
          ) : null}
          <TextField
            select
            size="small"
            label={t("admin.fundsHolder")}
            value={booking.funds_holder || "sv"}
            disabled={savingFundsHolder}
            helperText={t("admin.fundsHolderHint")}
            onChange={async (event) => {
              const next = event.target.value;
              setSavingFundsHolder(true);
              try {
                await updateBookingFundsHolder(booking.id, next);
                setBooking((current) => ({
                  ...current,
                  funds_holder: next,
                  legs: (current.legs || []).map((leg) => ({
                    ...leg,
                    funds_holder: next,
                  })),
                }));
              } catch (err) {
                enqueueSnackbar(
                  err?.response?.data?.error || t("admin.fundsHolderSaveError"),
                  { variant: "error" }
                );
              } finally {
                setSavingFundsHolder(false);
              }
            }}
            sx={{ minWidth: 240 }}
          >
            <MenuItem value="sv">{t("admin.fundsHolderSv")}</MenuItem>
            <MenuItem value="agent">{t("admin.fundsHolderAgent")}</MenuItem>
            <MenuItem value="carrier">{t("admin.fundsHolderCarrier")}</MenuItem>
          </TextField>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              {t("admin.comment")}
            </Typography>
            {booking.comment && !commentEditMode ? (
              <Button size="small" onClick={() => setCommentEditMode(true)}>
                {t("adminDetails.edit")}
              </Button>
            ) : null}
          </Box>
          {commentEditMode || !booking.comment ? (
            <Box>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={
                  booking.comment
                    ? t("adminDetails.commentPlaceholder")
                    : t("adminDetails.addComment")
                }
                sx={{ mb: 1 }}
              />
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={saveComment}
                  disabled={updatingComment || !comment.trim()}
                >
                  {booking.comment ? t("adminDetails.save") : t("adminDetails.addCommentBtn")}
                </Button>
                {commentEditMode ? (
                  <Button
                    size="small"
                    onClick={() => {
                      setComment(booking.comment || "");
                      setCommentEditMode(false);
                    }}
                  >
                    {t("admin.cancel")}
                  </Button>
                ) : null}
              </Stack>
            </Box>
          ) : (
            <Typography color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
              {booking.comment || t("adminDetails.noComment")}
            </Typography>
          )}
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 3 }}>
          {canCancel ? (
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                setCancelLegIndex(null);
                setCancelDialogOpen(true);
              }}
            >
              {t("adminDetails.cancelBooking")}
            </Button>
          ) : null}
          {canMarkNoShow ? (
            <Button
              variant="contained"
              color="warning"
              onClick={() => setNoShowDialogOpen(true)}
            >
              {t("admin.markNoShow")}
            </Button>
          ) : null}
        </Stack>
      </Paper>

      <Dialog
        open={cancelDialogOpen}
        onClose={() => {
          setCancelDialogOpen(false);
          setCancelLegIndex(null);
        }}
      >
        <DialogTitle>{t("adminDetails.confirmCancelTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {cancelLegIndex
              ? t("adminDetails.confirmCancelDirectionText", {
                  id: booking.id,
                  direction:
                    cancelLegIndex === 2
                      ? t("roundTrip.return")
                      : t("roundTrip.outbound"),
                })
              : t("adminDetails.confirmCancelText", { id: booking.id })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCancelDialogOpen(false);
              setCancelLegIndex(null);
            }}
          >
            {t("adminDetails.dismiss")}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={canceling}
            onClick={async () => {
              try {
                setCanceling(true);
                await manualCancelBookingById(booking.id, cancelLegIndex);
                enqueueSnackbar(
                  cancelLegIndex
                    ? t("adminDetails.directionCancelled")
                    : t("adminDetails.bookingCancelled"),
                  { variant: "success" }
                );
                setCancelDialogOpen(false);
                setCancelLegIndex(null);
                try {
                  await reloadBooking();
                } catch {
                  /* the cancel already succeeded; the next open refetches */
                }
              } catch (err) {
                enqueueSnackbar(
                  `${t("adminDetails.cancelError")} ${err.response?.data?.error || ""}`,
                  { variant: "error" }
                );
              } finally {
                setCanceling(false);
                setCancelDialogOpen(false);
              }
            }}
          >
            {t("adminDetails.confirmCancel")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(refundLeg)} onClose={() => setRefundLeg(null)}>
        <DialogTitle>{t("adminDetails.confirmRefundTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("adminDetails.confirmRefundDirectionText", {
              id: booking.id,
              direction:
                refundLeg?.leg_index === 2
                  ? t("roundTrip.return")
                  : t("roundTrip.outbound"),
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundLeg(null)}>{t("adminDetails.dismiss")}</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={refunding}
            onClick={async () => {
              try {
                setRefunding(true);
                const response = await refundBooking(booking.id, refundLeg.leg_index);
                enqueueSnackbar(
                  response.refund_amount != null
                    ? `${t("adminDetails.directionRefunded")}: ${response.refund_amount}`
                    : t("adminDetails.directionRefunded"),
                  { variant: "success" }
                );
                setRefundLeg(null);
                try {
                  await reloadBooking();
                } catch {
                  /* the refund request already succeeded */
                }
              } catch (err) {
                enqueueSnackbar(
                  err.message ||
                    err.response?.data?.error ||
                    t("adminDetails.refundError"),
                  { variant: "error" }
                );
              } finally {
                setRefunding(false);
              }
            }}
          >
            {t("adminDetails.confirmRefund")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmLeg)} onClose={() => setConfirmLeg(null)}>
        <DialogTitle>{t("adminDetails.confirmLiqpayTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmLeg?.transfer
              ? t("adminDetails.confirmLiqpayTransferText", {
                  id: booking.id,
                  amount: confirmLeg?.refund_amount,
                })
              : confirmLeg?.whole
              ? t("adminDetails.confirmLiqpayBookingText", {
                  id: booking.id,
                  amount: confirmLeg?.refund_amount,
                })
              : t("adminDetails.confirmLiqpayText", {
                  id: booking.id,
                  amount: confirmLeg?.refund_amount,
                  direction:
                    confirmLeg?.leg_index === 2
                      ? t("roundTrip.return")
                      : t("roundTrip.outbound"),
                })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmLeg(null)}>{t("adminDetails.dismiss")}</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={confirmingRefund}
            onClick={async () => {
              try {
                setConfirmingRefund(true);
                await confirmRefundBooking(
                  booking.id,
                  confirmLeg.whole ? undefined : confirmLeg.leg_index
                );
                enqueueSnackbar(t("adminDetails.liqpayRefundSent"), {
                  variant: "success",
                });
                setConfirmLeg(null);
                try {
                  await reloadBooking();
                } catch {
                  /* the LiqPay refund was already sent */
                }
              } catch (err) {
                enqueueSnackbar(
                  err.message || t("adminDetails.refundError"),
                  { variant: "error" }
                );
              } finally {
                setConfirmingRefund(false);
              }
            }}
          >
            {t("adminDetails.confirmLiqpay")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={noShowDialogOpen}
        onClose={() => {
          setNoShowDialogOpen(false);
          setNoShowComment("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("admin.markNoShow")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("admin.markNoShowDesc")}
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={noShowComment}
            onChange={(e) => setNoShowComment(e.target.value)}
            placeholder={t("admin.commentPlaceholder")}
            label={t("admin.comment")}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setNoShowDialogOpen(false);
              setNoShowComment("");
            }}
          >
            {t("adminDetails.dismiss")}
          </Button>
          <Button
            color="warning"
            variant="contained"
            disabled={markingNoShow}
            onClick={async () => {
              try {
                setMarkingNoShow(true);
                const response = await markNoShow(booking.id, noShowComment);
                enqueueSnackbar(t("adminDetails.markedNoShow"), {
                  variant: "success",
                });
                setBooking({
                  ...booking,
                  comment: response.comment || noShowComment,
                  passengers: (booking.passengers || []).map((p) => ({
                    ...p,
                    boarding_status: "no_show",
                  })),
                });
                setComment(response.comment || noShowComment || "");
                setNoShowDialogOpen(false);
                setNoShowComment("");
              } catch (err) {
                const errorMsg =
                  err?.response?.data?.error || t("adminDetails.markNoShowError");
                enqueueSnackbar(errorMsg, { variant: "error" });
              } finally {
                setMarkingNoShow(false);
              }
            }}
          >
            {t("admin.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
